import _ from 'lodash';
import { Fragment, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import cx from "classnames";

import { getMinDateLimitBehaviour, getQuery, formatAddress, copyToClipboard, getEventColor } from 'Core/data/Helpers';
import { truncateText } from 'Utils/helpers';
import { Chip } from '@mui/material';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Icon from '@mui/material/Icon';
import { Button, Tooltip } from "@gpstrackit/react-controls";
import Filters from 'Components/Filters';

import RawMessageView from 'Modules/devices/.components/rawMessageView'

import InputDate from 'Components/InputDate';
import moment from 'moment';

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    troubleShootingEventsTabGetData,
    rawMessageViewOpenModal,
    showRawDataModal,
    clearRedux,
    genericExportSend,
} from "Redux/actions";

//views
import UserTroubleshooting from 'Modules/devices/.components/userTroubleshooting';
import ShowRawData from "Modules/rawData/.components/showRawData";
import MaintenanceWarningEvents from 'Modules/maintenanceWarningEvents/views/main';

//Styles
import "./menu.scss";

const RANGE_MODE_ROWS = 50;
const FORCE_CURRENT_WEEK_RANGE = false; // true = always use current week dates on mount, ignoring saved preferences

const TroubleshootingEvents = (props) => {
    const { timezone, device, height, className, companyName, fromVidFleet } = props;
    const tableRef = useRef();
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);
    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(FORCE_CURRENT_WEEK_RANGE ? tempStartDate : (rawDataFilters?.startDate || tempStartDate));
    const [endDate, setEndDate] = useState(FORCE_CURRENT_WEEK_RANGE ? new Date() : (rawDataFilters?.endDate || new Date()));
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: startDate,
        end: endDate
    })

    const [filters, setFilters] = useState({
        startDate: startDate,
        endDate: endDate,
        page: 1,
        rowsPeerPage: 20,
        filterByDate: true,
        limit: 100
    })

    //Redux States
    const { loading, data, nextToken, conditions } = useSelector(state => state.troubleShootingEventsRedux);
    const open = useSelector(state => state.rawMessageViewRedux.openModal);
    const events = useSelector(state => state.generalRedux.events);

    let lastRawData = rawDataFilters?.last || null
    lastRawData = lastRawData ? _.toInteger(lastRawData.id) : null;
    if (lastRawData > 100)
        lastRawData = { id: '100', name: '100' }
    else
        lastRawData = rawDataFilters?.last
    //Local States
    const [rawData, setRawData] = useState("");
    const [tab, setTab] = useState(0);
    const [tabModule, setTabModule] = useState(0);
    const [currentTags, setCurrentTags] = useState([]);
    const [last, setLast] = useState(lastRawData || { id: '20', name: '20' });
    const [type, setType] = useState('range');

    const formatDateWithTimezone = (unixTimestamp, timezone) => {
        let formatString = 'MMM DD YYYY hh:mm:ss A';
        if (!timezone) {
            const localTime = moment.unix(unixTimestamp / 1000);
            return localTime.format(formatString);
        } else {
            const adjustedTime = moment.unix(unixTimestamp / 1000).tz(timezone)
            return `${adjustedTime.format(formatString)} ${moment.tz(timezone).format('z')}`
        }
    }

    const dateFormat = () => {
        return `
            let timeZone = '${timezone}'
            const formatDateWithTimezone = (unixTimestamp, timezone) => {
                let formatString = 'YYYY/MM/DD HH:mm:ss';
                if (!timezone) {
                    const localTime = moment.unix(unixTimestamp / 1000);
                    return localTime.format(formatString);
                } else {
                    const adjustedTime = moment.unix(unixTimestamp / 1000).tz(timezone)
                    return adjustedTime.format(formatString)
                }
            }
            return row.unitTime ? formatDateWithTimezone(+row.unitTime, timeZone) : ""
        `
    }

    function handleRawMessage(response) {
        if (typeof response === 'object' && response !== null) {
            return JSON.stringify(response);
        } else if (typeof response === 'string') {
            try {
                const parsedResponse = JSON.parse(response);
                return JSON.stringify(parsedResponse);
            } catch (e) {
                return response;
            }
        } else {
            console.error("Response format not supported");
            return null;
        }
    }

    const [columns, setColumns] = useState([
        {
            key: 'raw-message-view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Tooltip title="View Raw Message">
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(rawMessageViewOpenModal(true, item))
                        }}>
                            remove_red_eye
                        </Icon>
                    </Tooltip>

                )
            }
        },
        {
            key: 'unitTime',
            field: 'unitTime',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_unit_time"],
            width: 300,
            exportCell: "unitTime",
            export: true,
            functionString: dateFormat(),
            code: "trouble_shooting_events_tab_unit_time",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.unitTime && formatDateWithTimezone(+dataItem.unitTime, timezone)
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'datacenter',
            field: 'datacenter',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_data_center"],
            width: 300,
            exportCell: "datacenter",
            export: true,
            functionString: 'return row?.datacenter',
            code: "trouble_shooting_events_tab_data_center",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.datacenter || ""
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'wiredBatteryVoltage',
            field: 'wiredBatteryVoltage',
            show: true,
            locked: false,
            title: messages['trouble_shooting_events_tab_voltage'],
            width: 150,
            exportCell: "wiredBatteryVoltage",
            export: true,
            functionString: 'return row?.wiredBatteryVoltage',
            code: "trouble_shooting_events_tab_voltage",
        },
        {
            key: 'latitude',
            field: 'latitude',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_latitude"],
            width: 200,
            exportCell: "latitude",
            export: true,
            functionString: 'return row?.latitude',
            code: "trouble_shooting_events_tab_latitude",
        },
        {
            key: 'longitude',
            field: 'longitude',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_longitude"],
            width: 200,
            exportCell: "longitude",
            export: true,
            functionString: 'return row?.longitude',
            code: "trouble_shooting_events_tab_longitude",
        },
        {
            key: 'eventCode',
            field: 'eventCode',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_event_code"],
            width: 150,
            exportCell: "eventCode",
            export: true,
            functionString: 'return row?.eventCode',
            code: "trouble_shooting_events_tab_event_code",
        },
        {
            key: 'eventName',
            field: 'eventName',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_event_name"],
            width: 150,
            exportCell: "eventName",
            export: true,
            functionString: 'return row?.eventName',
            code: "trouble_shooting_events_tab_event_name",
            render: (dataItem) => {
                return (
                    <>
                        {
                            dataItem.eventName &&
                            <span style={{ color: `${getEventColor(events, dataItem.eventName)}` }}>
                                {dataItem.eventName}
                            </span>
                        }
                    </>
                )
            }
        },
        {
            key: 'heading',
            field: 'heading',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_heading"],
            width: 150,
            exportCell: "heading",
            export: true,
            functionString: 'return row?.heading',
            code: "trouble_shooting_events_tab_heading",
        },
        {
            key: 'sequenceNumber',
            field: 'sequenceNumber',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_sequence_number"],
            width: 200,
            exportCell: "sequenceNumber",
            export: true,
            functionString: 'return row?.sequenceNumber',
            code: "trouble_shooting_events_tab_sequence_number",
        },
        {
            key: 'speed',
            field: 'speed',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_speed"],
            width: 150,
            exportCell: "speed",
            export: true,
            functionString: 'return row?.speed',
            code: "trouble_shooting_events_tab_speed",
        },
        {
            key: 'address',
            field: 'address',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_address"],
            width: 350,
            exportCell: "address",
            export: true,
            functionString: 'return row?.address',
            code: "trouble_shooting_events_tab_address",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            formatAddress(dataItem.address) || ""
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'unitType',
            field: 'unitType',
            show: true,
            locked: false,
            title: messages["trouble_shooting_events_tab_unit_type"],
            width: 150,
            exportCell: "unitType",
            export: true,
            functionString: 'return row?.unitType',
            code: "trouble_shooting_events_tab_unit_type",
        },
        {
            key: 'rawMessage',
            show: true,
            title: messages["trouble_shooting_events_tab_raw_message"],
            width: 400,
            field: "rawMessage",
            export: true,
            resize: true,
            functionString: 'return row?.rawMessage',
            code: "trouble_shooting_events_tab_raw_message",
            render: (dataItem) => {
                let rawMessage =
                    dataItem.rawMessage && typeof dataItem.rawMessage === 'object' && !Array.isArray(dataItem.rawMessage)
                        ? handleRawMessage(dataItem.rawMessage)
                        : dataItem.rawMessage;
                return (
                    <Fragment>
                        <td>
                            {
                                dataItem.rawMessage &&
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="cursor-pointer pr-1"
                                        onClick={() => {
                                            showModal(rawMessage)
                                        }}
                                    >
                                        <Icon>
                                            search
                                        </Icon>
                                    </span>
                                    <span className="cursor-pointer pr-1"
                                        onClick={() => {
                                            copyToClipboard(rawMessage, messages['common_copied'])
                                        }}
                                    >
                                        <Icon style={{ fontSize: 19, margin: '0 3px 0px 0' }}>
                                            content_copy
                                        </Icon>
                                    </span>
                                    <span title={rawMessage?.length > 200 ? "Click search icon to view full message" : undefined}>
                                        {truncateText(rawMessage, 200)}
                                    </span>
                                </div>
                            }
                        </td>
                    </Fragment>
                )
            }
        }
    ])

    const options = [
        {
            id: 0,
            label: messages['trouble_shooting_events_tab_events'],

        },
        {
            id: 1,
            label: messages['trouble_shooting_events_tab_maintenance_warnings'],

        },
        {
            id: 2,
            label: messages['trouble_shooting_events_tab_user'],

        }

    ]

    const showModal = (raw) => {
        setRawData(raw);
        dispatch(showRawDataModal())
    }

    const loadInitData = (options = {}) => {
        const {
            conditions: sugestFilters = null,
            pagingFilters = null,
            reset = false,
            tags = null,
            rows = null,
            fromPreferences = false,
            type: paramType = undefined
        } = options;

        if (reset) {
            dispatch(clearRedux("TROUBLE_SHOOTING_EVENTS", { data: [], nextToken: null, conditions: null }))
        }

        let currentFilters = filters
        let curretTags = currentTags
        let currentType = paramType !== undefined ? paramType : type;
        // Last-n mode is no longer allowed — always force range
        if (currentType === 'last') {
            currentType = 'range';
        }

        if (sugestFilters) {
            currentFilters = sugestFilters;
        }

        if (tags)
            curretTags = tags

        if (pagingFilters) {
            currentFilters.page = pagingFilters.activePage;
            currentFilters.rowsPeerPage = pagingFilters.rowsPeerPage;
        }

        // Use local variables for dates to avoid stale state reads after setState
        let effectiveStartDate = startDate;
        let effectiveEndDate = endDate;

        // Sync local state from preferences when loaded from saved preferences
        const tz = timezone || device?.timezone || moment.tz.guess();
        if (fromPreferences && sugestFilters) {

            if (sugestFilters.filterByDate && sugestFilters.startDate) {

                currentType = 'range';
                setType('range');
                if (!FORCE_CURRENT_WEEK_RANGE) {
                    const newStartDate = new Date(moment.utc(sugestFilters.startDate).tz(tz).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    const newEndDate = new Date(moment.utc(sugestFilters.endDate).tz(tz).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    setStartDate(newStartDate);
                    setEndDate(newEndDate);
                    setBeforeRangeDate({
                        start: newStartDate,
                        end: newEndDate
                    });
                    // Use restored values directly for the query (not stale state)
                    effectiveStartDate = newStartDate;
                    effectiveEndDate = newEndDate;
                }
            } else if (sugestFilters.limit) {
                // Legacy 'last' preferences are migrated to range mode
                currentType = 'range';
                setType('range');
            }
        }

        // Always use date range filters (last-n mode is no longer supported)
        currentFilters.startDate = moment.tz(moment(effectiveStartDate).format("YYYY-MM-DD"), tz).startOf("day");
        currentFilters.endDate = moment.tz(moment(effectiveEndDate).format("YYYY-MM-DD"), tz).endOf("day");
        if (curretTags?.length > 0)
            currentFilters.conditions = [
                {
                    fields: ['eventCode', 'eventName'],
                    terms: curretTags.map((item) => item.id),
                    exact: false,
                    conditionOperator: "OR"
                }
            ];
        else {
            currentFilters.conditions = null;
        }

        setFilters({ ...currentFilters })
        setCurrentTags([...curretTags])

        const effectiveRows = RANGE_MODE_ROWS;

        // Always range mode — save filterByDate to preferences
        currentFilters.filterByDate = true;
        delete currentFilters.limit;

        dispatch(troubleShootingEventsTabGetData(
            device.id,
            device.companyId,
            reset ? "" : nextToken,
            effectiveRows,
            currentFilters
        ));
        if (!fromPreferences)
            tableRef?.current?.conditionsHandler(currentFilters);
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        setStartDate(newStart);
        setEndDate(newEnd);
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange);
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
            setStartDate(start);
            setEndDate(end);
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const isJsonString = (str) => {
        try {
            const parsed = JSON.parse(str);
            return typeof parsed === 'object' && parsed !== null;
        } catch {
            return false;
        }
    }

    const parsedRawData = isJsonString(rawData) ? JSON.parse(rawData) : rawData;

    const getGridFilter = () => {
        return (
            <div style={{ marginLeft: 10 }}>
                <Filters
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChangeFilters={() => loadInitData({ reset: true })}
                    disabled={loading}
                    single={props?.single}
                    module={props?.module}
                    setModule={props?.setModule}
                    onChange={onChange}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    //last={last}
                    //setLast={setLast}
                    type={type}
                    setType={(value) => {
                        setType(value);
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                        }
                        loadInitData({ reset: true, type: value })
                    }}
                    //lastOptionsLimit={100}
                    hideTypeSelection
                    onlyLast={false}
                />
            </div>
        )
    }
    let indexCount = 0;

    const getTroubleshootingGrid = () => {
        return (
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name="pm-trouble-shooting-events-v3"
                entity="troubleshooting-events-device"
                showTotalCount={false}
                infiniteScroll
                setColumns={(items) => setColumns(items)}
                extraButton={() => {
                    return getGridFilter();
                }}
                onChangePage={() => loadInitData()}
                tableButtons={["export", 'orderColumn']}
                onChangeColumns={(items) => setColumns(items)}
                conditions={{
                    ...conditions,
                    limit: RANGE_MODE_ROWS,
                    nextToken: null
                }}
                query={getQuery("trouble-shooting-events-v1")}
                noPDF
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort, key, fromPreferences) => {
                    loadInitData({ conditions, reset: true, tags, rows, fromPreferences });
                }}
                externalDataConfig={{
                    data: data || [],
                    loading: loading,
                    nextToken: nextToken,
                    onRefresh: (pagingFilters) => { loadInitData({ pagingFilters, reset: true }) },
                    totalCount: data?.length || 0,
                    totalCountLoading: loading,
                }}
                searchConfig={{
                    noAutomatic: true,
                    noOptions: true,
                    fixBar: true,
                    optionValue: "id",
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: false,
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "600px",
                }}
                selectionConfig={{
                    disabled: true,
                }}
                exportConfig={{
                    fileName: "troubleshootingevents",
                    isDynamo: true,
                    generate: (type) => {
                        const headers = columns
                            .filter(c => c.export)
                            .map(c => ({ header: c.title, column: c.key, functionString: c.functionString }));

                        const QUERY = `
                            query ($accountId: String!, $deviceId: String!, $dateRange: AWSDateTimeRange, $limit: Int, $lastRecords: Int, $conditions: [ TroubleshootingEventSearchInput ], $nextToken: String){
                                result: searchTroubleshootingEvents(accountId: $accountId, deviceId: $deviceId, dateRange: $dateRange, limit: $limit, lastRecords: $lastRecords, conditions: $conditions, nextToken: $nextToken) {
                                    items
                                    nextToken
                                }
                            }
                        `;

                        const variables = {
                            accountId: device.companyId + "",
                            deviceId: device.id + "",
                            dateRange: {
                                start: filters.startDate,
                                end: filters.endDate,
                            },
                            conditions: filters.conditions || null,
                        };

                        dispatch(genericExportSend(
                            type,
                            QUERY,
                            variables,
                            headers,
                            { unit: 'pt', size: 'A4', orientation: 'portrait', fontSize: 12 },
                            false,
                            true,
                            null,
                            "troubleshootingevents",
                            null,
                            null,
                        ));
                    },
                }}
            />
        )
    }

    // Force range mode — last-n is no longer supported.
    // Users who had 'last' saved in preferences are migrated on mount.
    useEffect(() => {
        if (type === 'last') {
            setType('range');
        }
    }, [type]);

    useEffect(() => {
        return () => {
            dispatch(clearRedux("TROUBLE_SHOOTING_EVENTS", { data: [], nextToken: null, conditions: null }))
        }
    }, [])

    useEffect(() => {
        dispatch(clearRedux("PREFERENCES", { returnServer: true }));
    }, [])

    // Initial data load is handled by DataViewInfiniteScroll's onChangeConditions
    // (via applyInitialPreferences). Removed redundant mount fetch to prevent double-fetch.

    useEffect(() => {
        if (data && data.error) {
            let message = messages["trouble_shooting_events_tab_load_error"]
            if (data?.error?.error) {
                if (messages[data.error.error])
                    message = messages[data.error.error]
                else
                    message = data.error.error.replace("Bad request ->", "").replace("GraphQL error: ", "")
            }
            NotificationManager.error(message, messages['trouble_shooting_events_tab_title'], 6000, null, null, '', null);
        }
    }, [data]);

    return (
        <div className="troubleshooting-events-tab">
            {
                fromVidFleet &&
                <div>
                    {getTroubleshootingGrid()}
                </div>
            }



            {
                !fromVidFleet &&
                <div className="vidfleet-menu">
                    {
                        options.map((option, index) => {
                            if (tabModule === option.id) {
                                return (
                                    <Chip
                                        className={cx("vidfleet-menu-general vidfleet-menu-pill-selected")}
                                        color="primary"
                                        label={option?.label || ''}
                                        key={index}
                                    />);
                            } else {
                                return (
                                    <Chip
                                        className={cx("vidfleet-menu-general vidfleet-menu-pill")}
                                        onClick={() => {
                                            setTabModule(option.id)
                                        }}
                                        label={option?.label || ''}
                                        key={index}
                                    />);
                            }
                        })
                    }
                </div>
            }

            {
                tabModule === 0 && !fromVidFleet &&
                <div>
                    {getTroubleshootingGrid()}
                </div>
            }

            {
                tabModule === 1 &&
                <MaintenanceWarningEvents
                    timezone={timezone}
                    tab={tab}
                    device={device}
                    tabMode
                    height={height}
                />
            }

            {
                tabModule === 2 &&
                <UserTroubleshooting
                    timezone={timezone}
                    tab={tab}
                    device={device}
                    height={height}
                    className={className}
                    companyName={companyName}
                />
            }

            {/* rawData modal */}
            <ShowRawData
                rawData={parsedRawData}
            />
            {open && <RawMessageView />}
        </div>
    );
}

export default TroubleshootingEvents;
